import React, { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Button, Box, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle
} from '@mui/material';
import { TextInput, NumberInput, BooleanInput, ReferenceInput, SelectInput, required, DateInput } from 'react-admin';
import { factoryMap } from "../../../core/resource/componentMap";
import { CarPlateField,CarPlateInput } from "./CarePlate";


const EditableJsonTable = ({ title, columns, initialData, source }) => {
  const { setValue, getValues, control } = useFormContext();
  const watchedData = useWatch({ name: source, control }) || initialData;

  const [localData, setLocalData] = useState(initialData); // Current editable data
  const [editSnapshot, setEditSnapshot] = useState(initialData); // Snapshot at edit start
  const [isEditable, setIsEditable] = useState(false); // Edit mode flag
  const [isModified, setIsModified] = useState(false); // Track if form is modified
  const [openDialog, setOpenDialog] = useState(false); // Dialog visibility state

  // Sync external data only if not in edit mode to avoid overwriting local edits
  useEffect(() => {
    if (!isEditable && JSON.stringify(watchedData) !== JSON.stringify(localData)) {
      //console.log("Syncing external data:", watchedData);
      setLocalData(watchedData); // Sync local data with external updates
      setEditSnapshot(watchedData); // Keep snapshot in sync
    }
  }, [watchedData, isEditable]);

  // Handle field change without affecting other fields
  const handleFieldChange = (field, value) => {
    setLocalData((prevData) => ({ ...prevData, [field]: value }));
    setIsModified(true); // Mark form as modified
  };

  const handleSave = () => {
    setValue(source, localData); // Save to form context
    setEditSnapshot(localData); // Update snapshot after saving
    setIsEditable(false); // Exit edit mode
    setIsModified(false); // Reset modified flag
    //console.log("Saved Data:", localData);
  };

  const handleUndo = () => {
    setLocalData(editSnapshot); // Restore snapshot data
    setIsModified(false); // Reset modified flag
  };

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  const confirmEdit = () => {
    setIsEditable(true); // Enable edit mode
    handleCloseDialog();
  };

  const renderInput = (column) => {
    const { source: fieldSource, label, type, disabled, field_meta } = column;
    const fieldValue = localData[fieldSource] || ''; // Use localData for value
    const isInputDisabled = !isEditable || disabled;

    switch (type) {
      case 'number':
        return (
          <NumberInput
            source={`${source}.${fieldSource}`}
            label={label}
            fullWidth
            value={fieldValue}
            onChange={(e) => handleFieldChange(fieldSource, e.target.value)}
            disabled={isInputDisabled}
          />
        );

      case 'boolean':
        return (
          <BooleanInput
            source={`${source}.${fieldSource}`}
            label={label}
            value={fieldValue}
            onChange={(e) => handleFieldChange(fieldSource, e.target.checked)}
            disabled={isInputDisabled}
          />
        );

      // Inside renderInput function
      case 'limited_number':

        const parseNumberInputToRecord = (v) => {
          const sanitizedValue = v.replace(/[^0-9]/g, ''); // Keep only digits
          //console.log({ sanitizedValue });
          return sanitizedValue;
        }

        return (
          <TextInput
            source={`${source}.${fieldSource}`}
            label={label}
            value={fieldValue}
            onChange={(e) => {
              const sanitizedValue = parseNumberInputToRecord(e.target.value);
              // if (sanitizedValue.length <= 10) {
              handleFieldChange(fieldSource, sanitizedValue); // Update state only if valid
              //   }
            }}

            disabled={isInputDisabled}
            fullWidth
            parse={parseNumberInputToRecord}
            variant="standard"


          // validate={[required(), validateTenDigits]} // Validate length and required field
          />
        );
      case 'date_input': // React-Admin DateInput component
        return (
          <DateInput
            source={`${source}.${fieldSource}`}
            label={label}
            value={fieldValue}
            onChange={(e) => handleFieldChange(fieldSource, e.target.value)}
            disabled={isInputDisabled}
            variant="standard"

            fullWidth
          />
        );

     

      case 'referance_field_input_new':
        const metaData = JSON.parse(field_meta);

        return (
          <ReferenceInput
            source={`${source}.${fieldSource}`}
            perPage={metaData.limit}
            reference={metaData.reference_resource}
            label={label}
            fullWidth

            sort={
              metaData &&
              metaData.sort_by && {
                field: metaData.sort_by,
                order: metaData.sort_order,
              }
            }
          >
            <SelectInput
              optionText={metaData.reference_source}
              value={fieldValue}
              variant="standard"
              validate={[required()]}


              onChange={(e) => handleFieldChange(fieldSource, e.target.value)}
              disabled={isInputDisabled}
            />
          </ReferenceInput>
        );

      case 'plate_number':
        return (
          <CarPlateInput
            source={`${source}.${fieldSource}`}
            label={label}
            value={fieldValue}
            fullWidth
            arabic
            type="transport"
            showEnglish={false}
            onChange={(e) => handleFieldChange(fieldSource, e.target.value)}
            disabled={isInputDisabled}
          />
        );

      default:
        return (
          <TextInput
            source={`${source}.${fieldSource}`}
            label={label}
            fullWidth
            value={fieldValue}
            variant="standard"

            onChange={(e) => handleFieldChange(fieldSource, e.target.value)}
            disabled={isInputDisabled}
          />
        );
    }
  };

  return (
    <Box>
      <TableContainer component={Paper} style={{ maxHeight: '400px', overflow: 'auto' }}>
        <Typography variant="h6" style={{ margin: '1em' }}>{title}</Typography>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.source}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.source}>{renderInput(column)}</TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="space-between" marginTop="1em">
        {isEditable ? (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!isModified}
            >
              حفظ {title}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleUndo}
              disabled={!isModified}
            >
              تراجع
            </Button>
          </>
        ) : (
          <Button variant="contained" color="primary" onClick={handleOpenDialog}>
            تعديل {title}
          </Button>
        )}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>تأكيد تعديل بيانات الخطة التشغيليه فى السجل الحالى</DialogTitle>
        <DialogContent>
          <DialogContentText>
            سيتم تعيين البيانات الى <strong>تم تعديل البيانات يدويا</strong> عند التعديل. هل تريد المتابعة؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">لا</Button>
          <Button onClick={confirmEdit} color="primary" autoFocus>نعم</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditableJsonTable;
